import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listChatModerationLog, listShiftThreadsForAdmin } from "@/lib/messages.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/chat-moderation")({
  head: () => ({ meta: [{ title: "Chat Moderation — The Hospitality Reserve" }] }),
  component: AdminChatModeration,
});

type LogRow = {
  id: string; message_id: string; shift_id: string; admin_user_id: string;
  action: string; reason: string | null; original_body: string | null;
  original_attachment_path: string | null; created_at: string;
};

function AdminChatModeration() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const loadLog = useServerFn(listChatModerationLog);
  const loadThreads = useServerFn(listShiftThreadsForAdmin);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"threads" | "log">("threads");
  const [log, setLog] = useState<LogRow[]>([]);
  const [threads, setThreads] = useState<Array<{ shift_id: string; last_message_at: string }>>([]);

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      const [l, t] = await Promise.all([loadLog({ data: { limit: 100 } }), loadThreads()]);
      if ("error" in l) toast.error(l.error); else setLog(l.rows as LogRow[]);
      if ("error" in t) toast.error(t.error); else setThreads(t.threads);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin</p>
            <h1 className="mt-3 font-display text-3xl">Chat Moderation</h1>
            <p className="mt-2 text-sm text-muted-foreground">Review live chat threads and an immutable audit trail of removals.</p>
          </div>
          <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back</Link>
        </div>
        <span className="gold-divider mt-4" />

        <div className="mt-6 flex gap-2 border-b border-border">
          {(["threads", "log"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs tracking-wide-luxury ${tab === t ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`}>
              {t === "threads" ? "Recent threads" : "Moderation log"}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : tab === "threads" ? (
          <div className="mt-6 space-y-2">
            {threads.length === 0 ? (
              <p className="text-sm text-muted-foreground">No chat activity yet.</p>
            ) : threads.map((t) => (
              <Link key={t.shift_id} to="/shift/$id/notes" params={{ id: t.shift_id }}
                className="block border border-border bg-card p-3 hover:border-accent">
                <p className="text-xs tracking-wide-luxury text-muted-foreground">SHIFT {t.shift_id.slice(0, 8)}</p>
                <p className="mt-1 text-sm">Last message: {new Date(t.last_message_at).toLocaleString()}</p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {log.length === 0 ? (
              <p className="text-sm text-muted-foreground">No moderation actions yet.</p>
            ) : log.map((r) => (
              <div key={r.id} className="border border-border bg-card p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs tracking-wide-luxury text-destructive">{r.action.toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Admin {r.admin_user_id.slice(0, 8)} · Shift {r.shift_id.slice(0, 8)} · Msg {r.message_id.slice(0, 8)}
                </p>
                {r.reason && <p className="mt-2 text-sm"><span className="text-muted-foreground">Reason:</span> {r.reason}</p>}
                {r.original_body && (
                  <p className="mt-1 text-sm"><span className="text-muted-foreground">Original:</span> <span className="italic">{r.original_body}</span></p>
                )}
                {r.original_attachment_path && (
                  <p className="mt-1 text-xs text-muted-foreground">Attachment removed: {r.original_attachment_path}</p>
                )}
                <Link to="/shift/$id/notes" params={{ id: r.shift_id }}
                  className="mt-2 inline-block text-xs tracking-wide-luxury text-accent hover:underline">View thread →</Link>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
